import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardBody,
  Text,
  Switch,
  HStack,
  VStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
  Icon,
  Divider,
  Tag,
  TagLabel,
  TagCloseButton,
  Spinner,
  Radio,
  RadioGroup,
  Stack
} from '@chakra-ui/react';
import { fetchWrapper } from '../../../../helpers';
import { BsFillPeopleFill } from 'react-icons/bs';
import Select from "react-select";
import CustomBadge from '../../../../components/SharedComponent/Badge';
import { getUserTimeZone } from '../../../WebHook-Logs';
import moment from 'moment';
import { SubscribersTable } from './SubscriberTable';


export const TargetContacts = ({ formData, setFormData, editable }) => {
  const [sendToAll, setSendToAll] = useState(formData.sendToAll || false);
  const [audienceType, setAudienceType] = useState(formData.audienceType || 'all_contacts');

  const [selectedTags, setSelectedTags] = useState(formData.selectedTags || []);
  const [allContacts, setAllContacts] = useState([]);
  const [AllContactsTotal, setAllContactsTotal] = useState({});
  const [pagination, setPagination] = useState({});
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [loadingTags, setLoadingTags] = useState(false);
  const [cohorts, setCohorts] = useState([]);
  const [loadingCohorts, setLoadingCohorts] = useState(false);
  const [selectedCohort, setSelectedCohort] = useState(null);
  const isInitialMount = useRef(true);



  const getConsistentColor = (str) => {
    if (!str) return { bg: '#F7FAFC', border: '#CBD5E0', text: '#4A5568' };
    // Array of vibrant color combinations (background, border, text)
    const colors = [
      { bg: '#F0E7FF', border: '#9B51E0', text: '#6B21A8' },      // Purple
      { bg: '#FFE7F0', border: '#E91E8C', text: '#BE185D' },      // Pink
      { bg: '#E7F0FF', border: '#2D9CDB', text: 'var(--brand-primary-active)' },      // Blue
      { bg: '#E7FFFF', border: '#27AE60', text: '#047857' },      // Cyan
      { bg: '#E7FFF4', border: '#219653', text: '#065F46' },      // Teal/Green
      { bg: '#FFF4E7', border: '#F2994A', text: '#C2410C' },      // Orange
      { bg: '#FFE7E7', border: '#EB5757', text: '#B91C1C' },      // Red
      { bg: '#FFFAE7', border: '#F2C94C', text: '#A16207' },      // Yellow
      { bg: '#F3E7FF', border: '#BB6BD9', text: '#7E22CE' },      // Light Purple
      { bg: '#E7FFF9', border: '#56CCF2', text: '#0E7490' },      // Light Cyan
      { bg: '#FFE7EB', border: '#F2709C', text: '#BE123C' },      // Light Pink
      { bg: '#E7F4FF', border: '#2F80ED', text: 'var(--brand-primary-hover)' },      // Sky Blue
      { bg: '#F9E7FF', border: '#C471ED', text: '#86198F' },      // Violet
      { bg: '#FFF0E7', border: '#FF6B35', text: '#C2410C' },      // Coral
      { bg: '#E7FFE9', border: '#6FCF97', text: '#15803D' },      // Light Green
    ];

    // Simple hash function to convert string to number
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }

    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };



  // Check if template has mobile channels (Push or In-App)
  const hasMobileChannels = () => {
    // In edit mode, audience_type may already be all_subscribers even if template lacks channel_id
    if (formData.audienceType === 'all_subscribers') return true;
    try {
      const channelIds = JSON.parse(formData.selectedTemplate?.channel_id || '[]');
      return channelIds.includes(6) || channelIds.includes(5);
    } catch (error) {
      console.error('Error parsing channel_id:', error);
      return false;
    }
  };

  useEffect(() => {
    const loadCohorts = async () => {
      setLoadingCohorts(true);
      try {
        const url = `${process.env.REACT_APP_API_URL}/cohorts`;
        const data = await fetchWrapper.get(url);

        // Handle different response structures safely
        const cohortsList = data.data?.cohorts || data.cohorts || data || [];
        setCohorts(cohortsList);
      } catch (err) {
        console.error("Failed to fetch cohorts", err);
        setCohorts([]);
      } finally {
        setLoadingCohorts(false);
      }
    };


    loadCohorts();
  }, []);
  // Load all available tags
  const loadTags = async () => {
    setLoadingTags(true);
    try {
      const url = `${process.env.REACT_APP_API_URL}/tags`;
      const data = await fetchWrapper.get(url);

      // Handle different response structures
      const tags = data.data || data.tags || data || [];
      setAvailableTags(tags);
    } catch (err) {
      console.error('Failed to fetch tags', err);
      setAvailableTags([]);
    } finally {
      setLoadingTags(false);
    }
  };

  // Load all contacts (for "send to all" option)
  const loadAllContacts = async (type = audienceType) => {
    setLoadingContacts(true);
    try {
      const url = `${process.env.REACT_APP_API_URL}/contacts?audience_type=${type}`;
      const data = await fetchWrapper.get(url);

      // Handle different response structures
      const contacts = data.data || data.contacts || data || [];
      setAllContacts(contacts);
      setPagination(data.pagination);
      setAllContactsTotal(data?.pagination)
      setFormData(prev => ({
        ...prev,
        allContactsTotal: data?.pagination,
        audience_type: type
      }));
    } catch (err) {
      console.error('Failed to fetch all contacts', err);
      setAllContacts([]);
    } finally {
      setLoadingContacts(false);
    }
  };

  // Load mobile subscribers
  const loadSubscribers = async () => {
    setLoadingContacts(true);
    try {
      const url = `${process.env.REACT_APP_API_URL}/subscriber/list?page=1&limit=100`;
      const data = await fetchWrapper.get(url);

      // Handle different response structures
      const subscribers = data.data || data.subscribers || data?.data?.subscribers || data || [];
      setAllContacts(subscribers);
      setPagination(data.pagination || data.data?.pagination);
      setAllContactsTotal(data.pagination || data.data?.pagination);
      setFormData(prev => ({
        ...prev,
        allContactsTotal: data.pagination || data.data?.pagination,
        audience_type: 'all_subscribers'
      }));
    } catch (err) {
      console.error('Failed to fetch subscribers', err);
      setAllContacts([]);
    } finally {
      setLoadingContacts(false);
    }
  };

  // Load contacts filtered by selected tags
  const loadContactsByTags = async (tagIds) => {
    if (!tagIds || tagIds.length === 0) {
      setFilteredContacts([]);
      return;
    }

    setLoadingContacts(true);
    try {
      // Join tag IDs with comma as shown in your curl example
      const tagIdString = tagIds.join(',');
      const url = `${process.env.REACT_APP_API_URL}/contacts?tagId=${encodeURIComponent(tagIdString)}`;
      const data = await fetchWrapper.get(url);

      // Handle different response structures
      const contacts = data.data || data.contacts || data || [];
      setFilteredContacts(contacts);
      setPagination(data.pagination);
      setFormData(prev => ({
        ...prev,
        taggedContactTotal: data?.pagination
      }));
    } catch (err) {
      console.error('Failed to fetch contacts by tags', err);
      setFilteredContacts([]);
    } finally {
      setLoadingContacts(false);
    }
  };
  const loadContactsByCohort = async (tagIds) => {
    if (!selectedCohort?._id) {
      setFilteredContacts([]);
      return;
    }

    setLoadingContacts(true);
    try {

      const url = `${process.env.REACT_APP_API_URL}/cohorts/${selectedCohort?._id}/contacts`;
      const data = await fetchWrapper.get(url);

      // Handle different response structures
      const contacts = data.data || data.contacts || data || [];

      setFilteredContacts(contacts?.contacts);
      setPagination(contacts.pagination);
      setFormData(prev => ({
        ...prev,
        cohortsContact: contacts?.pagination?.total_contacts
      }));
    } catch (err) {
      console.error('Failed to fetch contacts by tags', err);
      setFilteredContacts([]);
    } finally {
      setLoadingContacts(false);
    }
  };

  useEffect(() => {
    if (
      editable &&
      formData?.selectedCohort &&
      Object.entries(formData.selectedCohort).length > 0
    ) {
      setSelectedCohort(formData.selectedCohort);
    }
  }, [editable, formData.selectedCohort]);

  // Initial load
  useEffect(() => {
    if (
      !editable &&
      (!formData?.selectedCohort ||
        Object.entries(formData.selectedCohort).length <= 0)
    ) {
      if (audienceType === 'all_subscribers') {
        loadSubscribers();
      } else {
        loadAllContacts();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editable, formData.selectedCohort]);

  useEffect(() => {
    if (
      editable &&
      (!formData?.selectedCohort ||
        Object.entries(formData.selectedCohort).length <= 0)
    ) {
      if (audienceType === 'all_subscribers') {
        loadSubscribers();
      } else {
        loadAllContacts();
      }
    }
    loadTags();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editable, formData.selectedCohort]);

  useEffect(() => {
    if (selectedCohort?._id) {
      loadContactsByCohort()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCohort?._id]);

  // Reload contacts when audience type changes (while sendToAll is enabled)
  useEffect(() => {
    if (sendToAll) {
      if (audienceType === 'all_subscribers') {
        loadSubscribers();
      } else {
        loadAllContacts(audienceType);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audienceType, sendToAll]);


  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const selectedTagIds = selectedTags.map(tag => tag?._id || tag.id || tag);

    if (sendToAll) {
      setFilteredContacts(allContacts);
    } else if (selectedTagIds.length > 0) {
      loadContactsByTags(selectedTagIds);
    } else {
      setFilteredContacts([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendToAll, selectedTags, allContacts, editable]);

  useEffect(() => {
    if (editable && availableTags.length > 0 && formData?.selectedTags?.length > 0) {
      const prefilledTags = formData.selectedTags.map(tagIdOrObj => {
        if (typeof tagIdOrObj === "object") return tagIdOrObj;
        return availableTags.find(tag => tag._id === tagIdOrObj) || null;
      }).filter(Boolean);

      setSelectedTags(prefilledTags);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editable, availableTags]);

  // Update form data whenever audience changes
  useEffect(() => {
    const targetAudience = sendToAll ? allContacts : filteredContacts;

    setFormData(prev => ({
      ...prev,
      sendToAll,
      audienceType,
      selectedTags,
      targetAudience,
      audienceCount: targetAudience.length,
    }));
  }, [sendToAll, audienceType, selectedTags, allContacts, filteredContacts, setFormData]);

  const handleSendToAllToggle = (isChecked) => {
    setSendToAll(isChecked);

    if (isChecked) {
      setSelectedTags([]);
      setSelectedCohort(null)
      // Load contacts based on audience type
      if (audienceType === 'all_subscribers') {
        loadSubscribers();

      } else {
        loadAllContacts(audienceType);
      }
      setFormData(prev => ({
        ...prev,
        selectedCohort: null
      }));
    }
  };

  const handleTagSelect = (tag) => {
    // Check if tag is already selected by comparing _id
    const isSelected = selectedTags.some(selectedTag => selectedTag._id === tag._id);

    if (isSelected) {
      // Remove tag object from selection
      setSelectedTags(selectedTags.filter(selectedTag => selectedTag._id !== tag._id));
    } else {
      // Add tag object to selection
      setSelectedTags([...selectedTags, tag]);
    }
    setSendToAll(false);
  };

  const removeTag = (tagToRemove) => {
    setSelectedTags(selectedTags.filter(tag => tag._id !== tagToRemove._id));
  };

  const isTagSelected = (tag) => {
    return selectedTags.some(selectedTag => selectedTag._id === tag._id);
  };

  const getTagColor = (tag) => {
    // Use the tag's color property if available, otherwise default to 'gray'
    return tag.color ? 'blue' : 'gray'; // Chakra UI colorScheme
  };

  // Get current audience for display
  const currentAudience = sendToAll ? allContacts : filteredContacts;
  const audienceCount = currentAudience.length || formData?.cohortsContact;
  const audienceMode = React.useMemo(() => {
    if (audienceType === 'all_subscribers') return 'subscribers';
    if (sendToAll) return 'all_contacts';
    if (selectedCohort?._id) return 'cohort';
    if (selectedTags.length > 0) return 'tags';
    return 'none';
  }, [audienceType, sendToAll, selectedCohort, selectedTags]);
  const totalAudienceCount = React.useMemo(() => {
    if (audienceMode === 'all_contacts' || audienceMode === 'subscribers') {
      return formData?.allContactsTotal?.total || 0;
    }
    if (audienceMode === 'tags') {
      return pagination?.total || pagination?.total_contacts || 0;
    }
    if (audienceMode === 'cohort') {
      return formData?.cohortsContact || 0;
    }
    return 0;
  }, [audienceMode, formData, pagination]);


  const PreviewTable = ({ title, contacts, total }) => (
    <Box>
      <Text fontSize="md" fontWeight="semibold" color="gray.800" mb={4}>
        {title}
      </Text>

      <Card border="1px" borderColor="gray.200">
        {loadingContacts ? (
          <Box textAlign="center" p={6}>
            <Spinner color="blue.500" />
            <Text fontSize="sm" color="gray.600" mt={2}>
              Loading contacts...
            </Text>
          </Box>
        ) : total === 0 ? (
          <Box textAlign="center" p={6}>
            <Text color="gray.500">No contacts found</Text>
          </Box>
        ) : (
          <>
            <Box overflowX="auto">
              <Table variant="simple">
                <Thead bg="gray.50">
                  <Tr>
                    <Th>Name</Th>
                    <Th>Contact</Th>
                    <Th>Tags</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {contacts.slice(0, 5).map((contact) => (
                    <Tr key={contact._id || contact.id}>
                      <Td>{contact.name || '-'}</Td>
                      <Td>
                        <VStack align="start" spacing={1}>
                          <Text fontSize="sm">{contact.phone}</Text>
                          <Text fontSize="sm" color="blue.600">
                            {contact.email || '-'}
                          </Text>
                        </VStack>
                      </Td>
                      <Td>
                        <HStack spacing={1} flexWrap="wrap">
                          {(contact.tags || []).map(tag => {
                            const colors = getConsistentColor(tag.name || tag.label);
                            return (
                              <Tag
                                key={tag._id || tag.id}
                                size="sm"
                                color={colors.text}
                                bg={colors.bg}
                                border={`1px solid ${colors.border}`}
                              >
                                {tag.name || tag.label}
                              </Tag>
                            );
                          })}
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>

            {total > 5 && (
              <Box p={4} bg="gray.50" textAlign="center">
                <Text fontSize="sm" color="gray.600">
                  And {Math.max(total - 5, 0)} more contacts
                </Text>
              </Box>
            )}
          </>
        )}
      </Card>
    </Box>
  );

  return (
    <Box>
      <Card>
        <VStack spacing={6} align="stretch">
          <Box>
            <Text fontSize="xl" fontWeight="semibold" color="gray.800" mb={2}>
              Select Target Audience
            </Text>
            <Text fontSize="sm" color="gray.600">
              Choose to send your campaign to all contacts or filter by specific tags to target a specific audience segment.
            </Text>
          </Box>

          {/* Audience Type Radio Selection */}
          <Box className='box-style-card'>
            <CardBody p={4}>
              <Text fontSize="md" fontWeight="semibold" color="gray.800" mb={4}>
                Select Audience Type
              </Text>
              <RadioGroup value={audienceType} onChange={setAudienceType}>
                <Stack spacing={4}>
                  <Box
                    p={4}
                    borderWidth="1px"
                    borderRadius="md"
                    borderColor={audienceType === 'all_contacts' ? 'blue.500' : 'gray.200'}
                    bg={audienceType === 'all_contacts' ? 'blue.50' : 'white'}
                    cursor="pointer"
                    transition="all 0.2s"
                    _hover={{
                      borderColor: 'blue.500',
                      bg: 'blue.50'
                    }}
                    onClick={() => setAudienceType('all_contacts')}
                  >
                    <HStack spacing={3}>
                      <Radio value="all_contacts" />
                      <VStack align="start" spacing={1} flex={1}>
                        <Text fontWeight="semibold" color="gray.800">
                          Contact Number
                        </Text>
                        <Text fontSize="sm" color="gray.600">
                          Send campaign to contacts in your database via any communication channel
                        </Text>
                      </VStack>
                    </HStack>
                  </Box>

                  {hasMobileChannels() && (
                    <Box
                      p={4}
                      borderWidth="1px"
                      borderRadius="md"
                      borderColor={audienceType === 'all_subscribers' ? 'blue.500' : 'gray.200'}
                      bg={audienceType === 'all_subscribers' ? 'blue.50' : 'white'}
                      cursor="pointer"
                      transition="all 0.2s"
                      _hover={{
                        borderColor: 'blue.500',
                        bg: 'blue.50'
                      }}
                      onClick={() => { setAudienceType('all_subscribers'); setSendToAll(true) }}
                    >
                      <HStack spacing={3}>
                        <Radio value="all_subscribers" />
                        <VStack align="start" spacing={1} flex={1}>
                          <Text fontWeight="semibold" color="gray.800">
                             Mobile Subscriber
                          </Text>
                          <Text fontSize="sm" color="gray.600">
                            Send campaign to mobile app users via Push Notifications and In-App messages only
                          </Text>
                        </VStack>
                      </HStack>
                    </Box>
                  )}
                </Stack>
              </RadioGroup>
            </CardBody>
          </Box>

          <Box className='box-style-card '>
            <CardBody p={4}>
              <HStack justify="space-between">
                <HStack spacing={3}>
                  <Icon viewBox="0 0 24 24" color="blue.500" boxSize={6}>
                    <path
                      fill="currentColor"
                      d="M12,5.5A3.5,3.5 0 0,1 15.5,9A3.5,3.5 0 0,1 12,12.5A3.5,3.5 0 0,1 8.5,9A3.5,3.5 0 0,1 12,5.5M5,8C5.56,8 6.08,8.15 6.53,8.42C6.38,9.85 6.8,11.27 7.66,12.38C7.16,13.34 6.16,14 5,14A3,3 0 0,1 2,11A3,3 0 0,1 5,8M19,8A3,3 0 0,1 22,11A3,3 0 0,1 19,14C17.84,14 16.84,13.34 16.34,12.38C17.2,11.27 17.62,9.85 17.47,8.42C17.92,8.15 18.44,8 19,8M5.5,18.25C5.5,16.18 8.41,14.5 12,14.5C15.59,14.5 18.5,16.18 18.5,18.25V20H5.5V18.25M0,20V18.5C0,17.11 1.89,15.94 4.45,15.6C3.86,16.28 3.5,17.22 3.5,18.25V20H0M24,20H20.5V18.25C20.5,17.22 20.14,16.28 19.55,15.6C22.11,15.94 24,17.11 24,18.5V20Z"
                    />
                  </Icon>
                  <Box>
                    <Text fontWeight="semibold" color="gray.800">
                      {audienceType === 'all_subscribers' ? 'Send to All Mobile Subscribers' : 'Send to All Contacts'}

                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      Target your entire contact database {(audienceType === 'all_subscribers' && formData.sendToAll === false) ? `` : `(${formData?.allContactsTotal?.total} contacts).`}
                    </Text>
                  </Box>
                </HStack>
                <Switch
                  isChecked={sendToAll}
                  onChange={(e) => handleSendToAllToggle(e.target.checked)}
                  colorScheme="blue"
                  size="lg"
                // disabled={selectedCohort?._id }
                />
              </HStack>
            </CardBody>
          </Box>

          <Box position="relative" mb={6}>
            <Divider />
            <Box
              position="absolute"
              top="50%"
              left="50%"
              transform="translate(-50%, -50%)"
              bg="white"
              px={4}
            >
              <Text fontSize="sm" color="gray.500" fontWeight="medium">
                {"Or select a cohort".toLocaleUpperCase()}
              </Text>
            </Box>
          </Box>

          {/* Cohort Select */}
          <Box>
            <Text fontSize="md" fontWeight="semibold" color="gray.800" mb={3}>
              Select a Cohort
            </Text>

            {loadingCohorts ? (
              <Box textAlign="center" p={4}>
                <Spinner color="blue.500" />
                <Text fontSize="sm" color="gray.600" mt={2}>
                  Loading cohorts...
                </Text>
              </Box>
            ) : (<div style={{ width: "30%" }}>
              <Select
                options={cohorts.map((c) => ({
                  label: `${c.name} (${c.user_count} users)`,
                  value: c?._id,
                  cohort: c, // attach full object for easy access
                }))}
                value={
                  selectedCohort
                    ? {
                      label: <span style={{ display: "flex", alignItems: "center" }}><BsFillPeopleFill style={{ marginRight: "5px" }} /> {selectedCohort.name} ({selectedCohort.user_count} users)</span>,
                      value: selectedCohort?._id,
                      cohort: selectedCohort,
                    }
                    : null
                }
                onChange={(option) => {
                  setSelectedCohort(option?.cohort || null); setFormData(prev => ({
                    ...prev,
                    selectedCohort: option?.cohort
                  }));
                }}
                isClearable
                placeholder="Select a cohort"
                isDisabled={sendToAll || formData?.selectedTags?.length > 0 || audienceType === 'all_subscribers'}
                styles={{
                  control: (base) => ({
                    ...base,
                    borderColor: "#CBD5E0",
                    boxShadow: "none",
                    "&:hover": { borderColor: "#A0AEC0" },
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 10,
                  }),
                }}
              />
            </div>
            )}

            {selectedCohort?._id && !sendToAll && (
              <Box
                p={4}
                border="1px solid"
                borderColor="var(--brand-tint-strong)"
                bg="var(--brand-tint)"
                borderRadius="md"
                mt={4}
              >
                <HStack spacing={3} mb={3}>
                  <Flex alignItems="start">
                    <BsFillPeopleFill color="var(--brand-primary)" style={{ marginRight: "5px", marginTop: "5px" }} />
                    <Flex direction={"column"}>

                      <Text fontWeight="semibold" color="var(--brand-900)">
                        {selectedCohort?.name}
                      </Text>
                      <Text fontSize="xs" color="var(--brand-primary-hover)">
                        {selectedCohort?.description}
                      </Text>
                      <Flex>
                        <Text fontSize="xs" color="var(--brand-primary-hover)" me={4}>
                          {selectedCohort?.user_count} users
                        </Text>
                        <Text fontSize="xs" color="var(--brand-primary-hover)" me={4}>
                          Created :{moment.utc(selectedCohort?.created_at).tz(getUserTimeZone()).format('DD-MM-YY ')}
                        </Text>

                        <CustomBadge
                          px={2}
                          py={.8}
                          borderRadius="full"
                          fontSize="xs"
                          fontWeight="medium"
                          color={selectedCohort?.status === 'active' ? '#016630' : '#894b00'}
                          bg={selectedCohort?.status === 'active' ? '#dcfce7' : '#fef9c2'}

                        >
                          {selectedCohort?.status === 'active' ? 'Active' : 'Inactive'}
                        </CustomBadge>
                      </Flex>
                    </Flex>
                  </Flex>
                </HStack>


              </Box>
            )}

          </Box>

          <Box position="relative">
            <Divider />
            <Box position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" bg="white" px={4}>
              <Text fontSize="sm" color="gray.500" fontWeight="medium">
                OR FILTER BY TAGS
              </Text>
            </Box>
          </Box>

          <Box>
            <Text fontSize="md" fontWeight="semibold" color="gray.800" mb={3}>
              Select Tags to Filter Contacts
            </Text>
            <Text fontSize="sm" color="gray.600" mb={4}>
              Choose specific tags to target only contacts with those tags
            </Text>

            {loadingTags ? (
              <Box textAlign="center" p={4}>
                <Spinner color="blue.500" />
                <Text fontSize="sm" color="gray.600" mt={2}>Loading tags...</Text>
              </Box>
            ) : (
              <Flex wrap="wrap" gap={3}>
                {availableTags.map((tag) => {
                  const isSelected = isTagSelected(tag);
                  const isDisabled = sendToAll || selectedCohort?._id || audienceType === 'all_subscribers';

                  return (
                    <Tag
                      key={tag._id || tag.id}
                      onClick={() => !isDisabled && handleTagSelect(tag)}
                      cursor={isDisabled ? "not-allowed" : "pointer"}
                      
                      fontSize="12px"
                      fontWeight="500"
                      lineHeight="1.4"
                      transition="all 0.2s"
                      opacity={isDisabled ? 0.5 : 1}
                      bg={isSelected && !isDisabled ? "var(--brand-primary)" : "white"}
                      color={isSelected && !isDisabled ? "white" : "gray.800"}
                      border="1px solid"
                      borderColor={isSelected && !isDisabled ? "blue.500" : "gray.300"}
                      _hover={
                        !isDisabled
                          ? {
                            shadow: "sm",
                            transform: "translateY(-1px)",
                            borderColor: isSelected ? "blue.600" : "gray.400",
                          }
                          : {}
                      }
                    >
                      {tag.name || tag.label}
                    </Tag>
                  );
                })}
              </Flex>

            )}
          </Box>
          {selectedTags.length <= 0 && !sendToAll && !selectedCohort?._id && (
            <Box className='box-style-card'>
              <CardBody p={4}>
                <HStack spacing={3} mb={3}>
                  <Flex alignItems={"center"}>
                    <BsFillPeopleFill color='var(--brand-primary)' style={{ marginRight: "5px" }} />
                    <Text fontWeight="semibold" color="blue.800">
                      Target Audience: 0 contacts
                    </Text>
                  </Flex>
                </HStack>

                <Text fontSize="sm" color="blue.700" mb={3}>
                  No tags selected - please select tags or enable "Send to All Contacts"
                </Text>


              </CardBody>
            </Box>
          )}
          {selectedTags.length > 0 && !sendToAll && (
            <Box className='box-style-card'>
              <CardBody p={4}>
                <HStack spacing={3} mb={3}>
                  <Flex alignItems={"center"}>
                    <BsFillPeopleFill color='var(--brand-primary)' style={{ marginRight: "5px" }} />
                    <Text fontWeight="semibold" color="blue.800">
                      Target Audience: {pagination?.total} contacts
                    </Text>
                  </Flex>

                </HStack>

                <Text fontSize="sm" color="blue.700" mb={3}>
                  Selected tags: {selectedTags.map(tag => tag.name || tag.label).join(', ')}
                </Text>

                <HStack spacing={2} flexWrap="wrap">
                  {selectedTags.map((tag) => {
                    const colors = getConsistentColor(tag.name || tag.label)
                    return (
                      <Tag
                        key={tag._id || tag.id}
                        size={"sm"}
                        bg={colors.bg}
                        color={colors.text}
                        border={`1px solid ${colors.border}`}
                      >
                        <TagLabel>{tag.name || tag.label}</TagLabel>
                        <TagCloseButton onClick={() => removeTag(tag)} />
                      </Tag>
                    )
                  })}
                </HStack>
              </CardBody>
            </Box>
          )}
          {selectedCohort?._id && !sendToAll && (
            <Box className='box-style-card'>
              <CardBody p={4}>
                <HStack spacing={3} mb={3}>
                  <Flex alignItems={"center"}>
                    <BsFillPeopleFill color='var(--brand-primary)' style={{ marginRight: "5px" }} />
                    <Text fontWeight="semibold" color="blue.800">
                      Target Audience: {formData?.cohortsContact} contacts
                    </Text>
                  </Flex>

                </HStack>

                <Text fontSize="sm" color="blue.700" mb={3}>
                  Selected cohort: {selectedCohort?.name}
                </Text>

                <HStack spacing={2} flexWrap="wrap">
                  {selectedTags.map((tag) => (
                    <Tag
                      key={tag._id || tag.id}
                      size="md"
                      borderRadius="full"
                      variant="solid"
                      colorScheme={getTagColor(tag)}
                      style={{
                        backgroundColor: tag.color || undefined
                      }}
                    >
                      <TagLabel>{tag.name || tag.label}</TagLabel>
                      <TagCloseButton onClick={() => removeTag(tag)} />
                    </Tag>
                  ))}
                </HStack>
              </CardBody>
            </Box>
          )}
          {audienceCount === 0 && (
            <Box border={"3px"} borderStyle={"dashed"} borderColor={"#ebe6e7"} p="16">
              <Flex justifyContent={"center"} alignItems={"center"} direction={"column"}>
                <BsFillPeopleFill color='#d1d5dc' fontSize={"27px"} />
                <Text color='black' fontSize={"sm"} fontWeight={"semibold"}>No Audience selected</Text>
                <Text fontSize={"xs"} color={"#6a7282"} fontWeight={"hairline"}>Select tags to filter contacts or enable "Send to All Contacts"</Text>
              </Flex>
            </Box>
          )}



          {audienceMode === 'all_contacts' && (
            <PreviewTable
              title="All Contacts Preview"
              contacts={allContacts}
              total={totalAudienceCount}
            />
          )}

          {audienceMode === 'subscribers' && (
            <SubscribersTable
              title="Mobile Subscribers Preview"
              subscribers={allContacts}   // your API response array
              total={formData?.allContactsTotal?.total || 0}
              loading={loadingContacts}
            />
          )}


          {audienceMode === 'tags' && (
            <PreviewTable
              title="Tagged Contacts Preview"
              contacts={filteredContacts}
              total={totalAudienceCount}
            />
          )}

          {audienceMode === 'cohort' && (
            <PreviewTable
              title={`Cohort Preview – ${selectedCohort?.name}`}
              contacts={filteredContacts}
              total={totalAudienceCount}
            />
          )}



        </VStack>
     </Card>
    </Box>
  );
};